import { ResellerReport, GetResellerReportsParams } from 'components/ResellerReportHistory/interfaces';
import { API_BASE_URL } from '../configuration/config';
import { BaseService } from './base-service';
import { parseBackendError } from 'common/utils/error-utils';

interface CursorResponse {
    data: ResellerReport[];
    nextToken: string | null;
}

/**
 * Service for Reseller Report History.
 * Calls: GET /reseller/reports, GET /reseller/reports/{id}/download
 */
export class ResellerReportService extends BaseService {
    public static instance = new ResellerReportService(API_BASE_URL);

    private readonly reportsPath = '/reseller/reports';

    /**
     * List reports with filters and cursor-based pagination.
     */
    public async getReports(params: GetResellerReportsParams): Promise<{ items: ResellerReport[]; nextToken: string | null }> {
        const query = new URLSearchParams();
        if (params.vendor) query.set('vendor', params.vendor);
        if (params.status) query.set('status', params.status);
        if (params.reportType) query.set('reportType', params.reportType);
        if (params.search) query.set('search', params.search);
        if (params.amazonPo) query.set('amazonPo', params.amazonPo);
        if (params.dateFrom) query.set('dateFrom', params.dateFrom);
        if (params.dateTo) query.set('dateTo', params.dateTo);
        if (params.limit !== undefined) query.set('limit', String(params.limit));
        if (params.nextToken) query.set('nextToken', params.nextToken);

        const response = await this.fetch(`${this.reportsPath}?${query.toString()}`);
        if (!response.ok) {
            await parseBackendError(response);
        }

        const data: CursorResponse = await response.json();
        return {
            items: data.data,
            nextToken: data.nextToken,
        };
    }

    /**
     * Get presigned S3 URL for downloading a report .xlsx file.
     */
    public async getDownloadUrl(reportTaxonomyId: string): Promise<string> {
        const response = await this.fetch(`${this.reportsPath}/${encodeURIComponent(reportTaxonomyId)}/download`);
        if (!response.ok) {
            await parseBackendError(response);
        }

        const { downloadUrl } = await response.json();
        return downloadUrl;
    }
}
